import { fold as foldEither } from 'fp-ts/lib/Either';

import { ENVIRONMENT } from 'domain/entity/environment';
import LicensePermissions, { LicensePermissionFeature, VISUALISATION_FEATURES } from 'domain/entity/LicensePermissions';
import { GetUseCase } from 'use-case/genericUseCase';

// TODO (DEX-22478): Remove this deployment gating once the AI Description feature is available for all deployments.
const AI_DESCRIPTION_PRE_DEPLOYMENTS = [179, 5387, 7524, 147, 293, 6077, 8954, 3920];
const AI_DESCRIPTION_PROD_DEPLOYMENTS = [179, 3920];

export type EnvironmentSignals = {
    env?: string;
    apiV3?: string;
    hostname: string;
};

const readEnvironmentSignals = (): EnvironmentSignals => ({
    env: process.env.ENV,
    apiV3: process.env.API_V3,
    hostname: window.location.hostname,
});

// The bundle is built once and deployed to both pre and prod, so build-time env vars alone
// cannot tell the environments apart — the runtime hostname check is what distinguishes them.
export const isPreEnvironment = (signals: EnvironmentSignals = readEnvironmentSignals()): boolean =>
    signals.env === ENVIRONMENT.DEVELOPMENT ||
    (signals.apiV3?.includes('pre.dexcell') ?? false) ||
    signals.hostname.endsWith('pre.dexma.com');

export const isAiDescriptionAllowedDeployment = (deploymentId: number, isPre: boolean = isPreEnvironment()): boolean =>
    (isPre ? AI_DESCRIPTION_PRE_DEPLOYMENTS : AI_DESCRIPTION_PROD_DEPLOYMENTS).includes(deploymentId);

export const isAiDescriptionFeatureGranted = (features?: LicensePermissionFeature[]): boolean =>
    features?.some((feature) => feature.name === VISUALISATION_FEATURES.AI_GENERATED_TEXT && feature.access === 'YES') ?? false;

export const isAiDescriptionAllowed = (
    deploymentId: number,
    features?: LicensePermissionFeature[],
    isPre: boolean = isPreEnvironment()
): boolean => isAiDescriptionAllowedDeployment(deploymentId, isPre) || isAiDescriptionFeatureGranted(features);

// One license-permissions request per page load, shared by every widget consulting the gate,
// so read-only views can honour license-granted accounts without a call per widget.
// Module scope is deliberate: the apps rebuild their dependency objects on every render
// (see getWidgetDataDependencies call sites), so caching by argument or React context
// identity would re-fetch on each render. A failed read resolves to no features.
let licenseFeaturesOnce: Promise<LicensePermissionFeature[]> | null = null;

export const readLicenseFeaturesOnce = (
    getReadLicensePermissions: GetUseCase<LicensePermissions, null>
): Promise<LicensePermissionFeature[]> => {
    if (licenseFeaturesOnce === null) {
        licenseFeaturesOnce = getReadLicensePermissions(null)
            .then(
                foldEither(
                    () => [] as LicensePermissionFeature[],
                    (licensePermissions: LicensePermissions) => licensePermissions.features
                )
            )
            .catch(() => []);
    }
    return licenseFeaturesOnce;
};

// Test hook: module state would otherwise leak between test cases.
export const resetLicenseFeaturesCache = (): void => {
    licenseFeaturesOnce = null;
};
